
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface AuthUser {
  id: string;
  email?: string;
  name?: string;
  username?: string;
  avatar_url?: string;
  role?: string;
}

export const useSupabaseAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Function to refresh session if it's about to expire
    const setupSessionRefresh = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          // Get expiry time
          const expiresAt = new Date(data.session.expires_at * 1000);
          const now = new Date();
          
          // Calculate time until session expiry (minus a buffer of 5 minutes)
          const timeUntilExpiry = expiresAt.getTime() - now.getTime() - (5 * 60 * 1000);
          
          if (timeUntilExpiry > 0) {
            // Set timeout to refresh token before it expires
            setTimeout(async () => {
              console.log('Refreshing auth session...');
              const { data, error } = await supabase.auth.refreshSession();
              if (error) {
                console.error('Error refreshing session:', error);
              } else if (data?.session) {
                console.log('Session refreshed successfully');
                // After refresh, set up the next refresh
                setupSessionRefresh();
              }
            }, timeUntilExpiry);
          } else {
            // If session is already expired or about to expire, refresh immediately
            const { error } = await supabase.auth.refreshSession();
            if (error) {
              console.error('Error refreshing session:', error);
            } else {
              setupSessionRefresh();
            }
          }
        }
      } catch (error) {
        console.error('Error in session refresh setup:', error);
      }
    };

    // Get the current session
    const getSession = async () => {
      setLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // Get user profile data
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, name, username, avatar_url, email, role')
            .eq('id', session.user.id)
            .single();
            
          if (profileError) {
            console.error('Error fetching profile:', profileError);
          }
          
          if (profileData) {
            setUser({
              id: session.user.id,
              email: session.user.email,
              name: profileData.name || undefined,
              username: profileData.username || undefined,
              avatar_url: profileData.avatar_url || undefined,
              role: profileData.role || undefined
            });
          } else {
            setUser({
              id: session.user.id,
              email: session.user.email
            });
          }
          
          // Setup session refresh mechanism
          setupSessionRefresh();
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    getSession();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event);
        if (event === 'SIGNED_IN' && newSession) {
          getSession();
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('Token refreshed successfully');
          getSession();
        }
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    user,
    loading,
    signOut: () => supabase.auth.signOut()
  };
};
